<template>
  <DsPageContent>
    <DsHero
      v-if="content"
      :title="content.title"
      theme="grey"
    />

    <KnowledgeHubFilters
      forced-reset
      @selected="handleFiltersUpdate"
    />

    <div
      v-if="highlightedArticles && highlightedArticles.length"
      class="ds-background-color-white"
    >
      <KnowledgeHubHighlightedArticles :highlighted-articles="highlightedArticles" />
    </div>

    <DsSection
      v-if="fixedTypeArticles && fixedTypeArticles.length"
      :full-screen="true"
    >
      <KnowledgeHubArticlesGallery
        :title="content.fixedArticlesTitle"
        :articles="fixedTypeArticles"
        :see-all-label="labels.seeAllKnowledgeHubArticles"
        :see-all-link="seeAllFixedTypeArticlesLink"
      />
    </DsSection>

    <DsSection
      v-if="highlightedArticleSolo"
      class="kh-highlighted-article"
      :with-space-top="false"
      :with-space-bottom="false"
    >
      <div class="ds-grid">
        <div class="ds-grid__col-12 ds-padding-x-12">
          <DsMediaTextSection
            :image="highlightedArticleSolo.imageWithSizes"
            :title="highlightedArticleSolo.title"
            :text="highlightedArticleSolo.shortDescription"
            :link="highlightedArticleSolo.link"
            link-color="green"
            theme="grey"
          />
        </div>
      </div>
    </DsSection>

    <DsSection
      v-if="latestArticles && latestArticles.length"
      :full-screen="true"
    >
      <KnowledgeHubArticlesGallery
        :title="content.latestPostsTitle"
        :articles="latestArticles"
        :see-all-label="labels.seeAllKnowledgeHubArticles"
        :see-all-link="seeAllLatestArticlesLink"
      />
    </DsSection>

    <DsSection
      v-if="(popularTopics && popularTopics.length) || (customArticles && customArticles.length)"
      theme="grey"
      :full-screen="true"
    >
      <KnowledgeHubPopularTopics
        v-if="popularTopics && popularTopics.length"
        :items="popularTopics"
        :title="content.popularTopicsTitle"
      />

      <DsSection
        v-if="customArticles && customArticles.length"
        theme="grey"
      >
        <div class="ds-grid">
          <div class="ds-grid__col ds-grid__col--12">
            <DsDivider />
          </div>
        </div>
      </DsSection>

      <KnowledgeHubArticlesGallery
        v-if="customArticles && customArticles.length"
        :title="content.customRelatedArticlesTitle"
        :articles="customArticles"
        :see-all-label="labels.seeAllKnowledgeHubArticles"
        :see-all-link="seeAllCustomArticlesLink"
      />
    </DsSection>

    <NewsletterForm
      v-if="newsletterFormData"
      v-bind="newsletterFormData"
      one-column-layout
      theme="light"
    />
  </DsPageContent>
</template>

<script setup>
import { DsDivider, DsHero, DsMediaTextSection, DsPageContent, DsSection } from 'awds'
import KnowledgeHubFilters from '../../components/knowledge-hub/knowledge-hub-filters.vue'
import KnowledgeHubArticlesGallery from '#layers/core/components/knowledge-hub/knowledge-hub-articles-gallery.vue'
import KnowledgeHubHighlightedArticles from '../../components/knowledge-hub/knowledge-hub-highlighted-articles.vue'
import KnowledgeHubPopularTopics from '../../components/knowledge-hub/knowledge-hub-popular-topics.vue'
import { PageKnowledgeHub, PageKnowledgeHubArticles } from '#layers/core/data-layer/definitions/knowledgeHub'
import FORM_NAME from '#layers/core/constants/globalForm'
import { queryPageKnowledgeHubSupplementaryData } from '../../data-layer/queries'
import { mapperBlockKnowledgeHubTopic } from '#layers/core/data-layer/blocks/mappers'
import { mapperKnowledgeHubArticlesSummaries } from '#layers/core/data-layer/knowledge-hub/summary/mapper-knowledge-hub-articles-summaries'
import { knowledgeHubFilterMap } from '../../constants/filters'

const { labels, localeGlobalContentData } = useKnowledgeHubGlobalData()
const { queryContentful } = useQueries()
const { getSlug } = usePageUtils()
const { useStandardAsyncData, onAfterAdyenData, onBeforeAdyenData, content } = useKnowledgeHubPageData()
const { locale } = useI18n()

const { getLocaleIso } = useLocales()
const localeIso = getLocaleIso(locale.value)

const store = useRoutesStore()
const route = useRoute()
const router = useRouter()
const { setTheme } = useTheme()

const slug = getSlug(route.path) || 'knowledge-hub'
const { sanitizeRoutePath } = useRoutesUtils()
const preview = useRuntimeConfig().public.preview

await onBeforeAdyenData({ globalDataContentType: PageKnowledgeHub.contentType })

const { data } = await useAsyncData('knowledge-hub-' + locale.value, async () => {
  const { pageData } = await useStandardAsyncData(PageKnowledgeHub, {
    pageSlug: slug
  })

  const { getRouteByEntry } = useRoutes(store.routes, locale)
  // TODO - Refactor
  const articlesPageRoute = getRouteByEntry('3kcR7YrWAsdeQAgbxHghYA').route
  const articleTypeId = pageData?.content?.fixedArticlesArticleType?.sys?.id || ''
  const customTagId = pageData?.content?.customRelatedArticlesTopic?.sys?.id || ''
  try {
    const supplementaryData = await queryContentful(
      {
        query: queryPageKnowledgeHubSupplementaryData,
        variables: { locale: localeIso, limit: 3, articleTypeId, customTagId, preview }
      },
      { log: false }
    )
    return {
      pageData,
      supplementaryData,
      newsletterFormData: localeGlobalContentData.value?.globalForms[FORM_NAME.newsletterKnowledgeHub],
      articlesPageRoute
    }
  } catch (e) {
    throw new Error(`Fetching ${locale.value} failed! - ${e}`)
  }
})

// Needed -  since if users route path is `/knowledge-hub/` and we use route.path (which is still `/knowledge-hub/`) and they select one of the filters, the url will be `/knowledge-hub//article`
const sanitizedRoutePath = computed(() => {
  return sanitizeRoutePath(route.path)
})

const newsletterFormData = computed(() => {
  return data.value?.newsletterFormData || {}
})

const mapperOptions = computed(() => {
  const { getUrl, getInternalUrl } = useRoutes(store.routes || [], locale)

  return {
    getUrl,
    getInternalUrl,
    locale: locale.value
  }
})

const supplementaryContent = computed(() => {
  return data.value?.supplementaryData
})

const highlightedArticles = computed(() => {
  return content.value?.highlightedTopArticlesCollection.filter(Boolean)
})

const highlightedArticleSolo = computed(() => {
  const mappedValue = content.value?.highlightBlock
  if (!mappedValue) {
    return null
  }
  return { ...mappedValue, link: { ...mappedValue.link, title: labels.value.readMore } }
})

const popularTopics = computed(() => {
  const size = { width: 300, height: 400, aspectRatio: '3/4' }
  const imageSizes = { small: size, medium: size, large: size }
  return content.value?.popularTopicsCollection
    .filter(Boolean)
    .map((item) => mapperBlockKnowledgeHubTopic(item, imageSizes, data.value?.articlesPageRoute, knowledgeHubFilterMap))
})

const latestArticles = computed(() => {
  return mapperKnowledgeHubArticlesSummaries(supplementaryContent?.value?.latestArticles?.items, mapperOptions.value)
})

const fixedTypeArticles = computed(() => {
  return mapperKnowledgeHubArticlesSummaries(supplementaryContent?.value.fixedTypeArticles?.items, mapperOptions.value)
})

const customArticles = computed(() => {
  return mapperKnowledgeHubArticlesSummaries(supplementaryContent?.value.customArticles?.items, mapperOptions.value)
})

const seeAllLatestArticlesLink = computed(() => {
  return data.value?.articlesPageRoute
})

const seeAllFixedTypeArticlesLink = computed(() => {
  return `${data.value?.articlesPageRoute}?articleType=${content.value.fixedArticlesArticleType.sys.id}`
})

const seeAllCustomArticlesLink = computed(() => {
  const filterKey = knowledgeHubFilterMap[content?.value?.customRelatedArticlesTopic?.contentType]
  const filterValue = content?.value?.customRelatedArticlesTopic?.sys?.id
  if (!filterKey || !filterValue) {
    return ''
  }
  return `${data.value?.articlesPageRoute}?${filterKey}=${filterValue}`
})

const handleFiltersUpdate = (filtersData) => {
  const queryParams = filtersData.reduce((prevValue, filter) => ({ ...prevValue, [filter.name]: filter.value }), {})
  router.push({ path: data.value?.articlesPageRoute, query: queryParams })
}

onBeforeMount(() => {
  setTheme('grey')
})
await onAfterAdyenData(data.value)
</script>
